import { useState } from 'react';
import { Loader2, Pencil } from 'lucide-react';
import { useToast } from '@mustauto/ui/web';
import { formatApiError } from '@mustauto/utils';
import { PAINT_CODE_MAX, PAINT_COLOR_MAX, PAINT_FORMULA_MAX, formatPaint } from '@mustauto/types';
import type { PaintInput } from '@/hooks/useServiceOrders';

const inputClass = 'block h-8 w-full rounded-md border border-input bg-background px-2 text-sm';

/**
 * Cor, código e fórmula da tinta no card do veículo (F3.6), editáveis.
 *
 * A fórmula nasce na hora de misturar, dias depois da abertura — por isso a
 * pintura é editável aqui, e não só na avaliação de dano do app. Campo vazio
 * APAGA (a rota recebe o formulário inteiro). O código gravado aqui também vai
 * para o cadastro do carro, se ele ainda não tem um.
 *
 * Sem código na OS mas com código no cadastro do carro, o campo abre com ele
 * sugerido: é o mesmo carro, e o código de fábrica não muda.
 */
export function OSPaintField({
  paintColor,
  paintCode,
  paintFormula,
  vehiclePaintCode,
  alwaysShow,
  readOnly = false,
  onSave,
  isSaving,
}: {
  paintColor: string | null;
  paintCode: string | null;
  paintFormula: string | null;
  vehiclePaintCode: string | null;
  /** OS de funilaria: o campo aparece mesmo vazio, como convite. */
  alwaysShow: boolean;
  /** Quem não registra a execução (`os.executar`) vê a cor, sem editar. */
  readOnly?: boolean;
  onSave: (paint: PaintInput) => Promise<unknown>;
  isSaving: boolean;
}) {
  const { toast } = useToast();
  const [editing, setEditing] = useState(false);
  const [color, setColor] = useState('');
  const [code, setCode] = useState('');
  const [formula, setFormula] = useState('');

  const label = formatPaint(paintColor, paintCode);
  if (!editing && !label && !paintFormula && !alwaysShow) return null;

  const start = () => {
    setColor(paintColor ?? '');
    setCode(paintCode ?? vehiclePaintCode ?? '');
    setFormula(paintFormula ?? '');
    setEditing(true);
  };

  const save = async () => {
    try {
      await onSave({
        paintColor: color.trim() || null,
        paintCode: code.trim() || null,
        paintFormula: formula.trim() || null,
      });
      setEditing(false);
    } catch (err) {
      toast({
        type: 'error',
        title: 'Não foi possível salvar a pintura',
        description: formatApiError(err, 'Tente novamente em instantes.'),
      });
    }
  };

  if (!editing && readOnly) {
    if (!label && !paintFormula) return null;
    return (
      <div className="col-span-2">
        <p className="text-xs text-muted-foreground">Cor da pintura</p>
        <span className="font-medium">{label ?? '—'}</span>
        {paintFormula && <span className="block text-xs text-muted-foreground">Fórmula: {paintFormula}</span>}
      </div>
    );
  }

  if (!editing) {
    return (
      <div className="col-span-2">
        <p className="text-xs text-muted-foreground">Cor da pintura</p>
        <button
          type="button"
          onClick={start}
          className="group flex items-start gap-1.5 text-left"
          aria-label="Editar a cor da pintura"
        >
          <span>
            <span className={label ? 'font-medium' : 'text-muted-foreground'}>{label ?? 'Informar a cor'}</span>
            {paintFormula && (
              <span className="block text-xs text-muted-foreground">Fórmula: {paintFormula}</span>
            )}
          </span>
          <Pencil size={12} className="mt-1 text-muted-foreground group-hover:text-foreground" aria-hidden />
        </button>
      </div>
    );
  }

  return (
    <div className="col-span-2 space-y-2 rounded-md border p-3">
      <p className="text-xs font-medium text-muted-foreground">Cor da pintura</p>
      <div className="grid grid-cols-5 gap-2">
        <label className="col-span-3 space-y-1">
          <span className="text-xs text-muted-foreground">Cor</span>
          <input
            className={inputClass}
            value={color}
            onChange={(e) => setColor(e.target.value)}
            maxLength={PAINT_COLOR_MAX}
            placeholder="Prata Metálico"
          />
        </label>
        <label className="col-span-2 space-y-1">
          <span className="text-xs text-muted-foreground">Código</span>
          <input
            className={`${inputClass} font-mono uppercase`}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            maxLength={PAINT_CODE_MAX}
            placeholder="1F7"
          />
        </label>
      </div>
      <label className="block space-y-1">
        <span className="text-xs text-muted-foreground">Fórmula / variante</span>
        <input
          className={inputClass}
          value={formula}
          onChange={(e) => setFormula(e.target.value)}
          maxLength={PAINT_FORMULA_MAX}
          placeholder="Variante 2 · fórmula 4471-B"
        />
      </label>
      {!paintCode && vehiclePaintCode && code === vehiclePaintCode && (
        <p className="text-xs text-muted-foreground">Código do cadastro do carro.</p>
      )}
      <div className="flex justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={() => setEditing(false)}
          className="h-8 rounded-md px-3 text-sm text-muted-foreground hover:text-foreground"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={() => void save()}
          disabled={isSaving}
          className="inline-flex h-8 items-center gap-1.5 rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground disabled:opacity-60"
        >
          {isSaving && <Loader2 size={14} className="animate-spin" aria-hidden />}
          Salvar
        </button>
      </div>
    </div>
  );
}
